'use client';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useLanguage } from '@/lib/LanguageContext';
import { useApp } from '@/lib/AppContext';
import { t } from '@/lib/i18n';
import { allLessonData, levelTests } from '@/lib/lessonData';
import LessonProgressBar from './LessonProgressBar';
import TheoryStep from './TheoryStep';
import ExampleStep from './ExampleStep';
import ExerciseStep from './ExerciseStep';
import QuizStep from './QuizStep';
import ResultStep from './ResultStep';
import LevelTestScreen from './LevelTestScreen';
import { X, BookOpen, Lock } from 'lucide-react';
import Link from 'next/link';

type StepType = 'theory' | 'example' | 'exercise' | 'quiz' | 'result';
const STEP_ORDER: StepType[] = ['theory', 'example', 'exercise', 'quiz', 'result'];

export default function LessonContent() {
  const { language } = useLanguage();
  const { completeLesson, completeLevelTest, userLevels, completedLevelTests } = useApp();
  const searchParams = useSearchParams();
  const lessonId = searchParams.get('lessonId') ?? 'l-01-01';
  const isLevelTest = searchParams.get('levelTest') === 'true';
  const levelTestNum = parseInt(searchParams.get('level') ?? '1', 10);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [exerciseAnswers, setExerciseAnswers] = useState<Record<string, string>>({});
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [exerciseChecked, setExerciseChecked] = useState(false);
  const [quizChecked, setQuizChecked] = useState(false);
  const [xpEarned, setXpEarned] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [lessonCompleted, setLessonCompleted] = useState(false);

  useEffect(() => {
    setCurrentStepIndex(0);
    setExerciseAnswers({});
    setQuizAnswers({});
    setExerciseChecked(false);
    setQuizChecked(false);
    setXpEarned(0);
    setAccuracy(0);
    setLessonCompleted(false);
  }, [lessonId, isLevelTest]);

  // Level test mode
  if (isLevelTest) {
    const test = levelTests.find((t) => t.levelNumber === levelTestNum);
    if (!test) {
      return (
        <div className="max-w-2xl mx-auto py-20 text-center">
          <p className="text-muted-foreground">{language === 'fr' ? 'Test introuvable' : 'Тест не найден'}</p>
          <Link href="/learning-path-screen" className="mt-4 inline-block text-primary underline">
            {language === 'fr' ? 'Retour au parcours' : 'Вернуться к пути'}
          </Link>
        </div>
      );
    }
    return (
      <LevelTestScreen
        test={test}
        language={language}
        onComplete={(score, passed) => completeLevelTest(levelTestNum, score, passed)}
        previousResult={completedLevelTests[levelTestNum]}
      />
    );
  }

  const lessonMeta = userLevels.flatMap((l) => l.lessons).find((ls) => ls.id === lessonId);
  const levelMeta = userLevels.find((l) => l.lessons.some((ls) => ls.id === lessonId));
  const lesson = allLessonData[lessonId] ?? allLessonData['l-01-01'];
  const currentStepType = STEP_ORDER[currentStepIndex];
  const currentStepData = lesson.steps.find((s) => s.type === currentStepType);
  const isLocked = lessonMeta?.status === 'locked';

  const handleNext = () => {
    if (currentStepIndex < STEP_ORDER.length - 1) setCurrentStepIndex((i) => i + 1);
  };
  const handleBack = () => {
    if (currentStepIndex > 0) setCurrentStepIndex((i) => i - 1);
  };
  const handleExerciseCheck = (answers: Record<string, string>) => {
    setExerciseAnswers(answers);
    setExerciseChecked(true);
  };
  const handleQuizComplete = (answers: Record<string, string>, acc: number) => {
    setQuizAnswers(answers);
    setQuizChecked(true);
    setAccuracy(acc);
    const baseXp = lessonMeta?.xpReward ?? 100;
    const earned = Math.round(baseXp * (acc / 100));
    setXpEarned(earned);
    if (!lessonCompleted) {
      setLessonCompleted(true);
      completeLesson(lessonId, earned, acc);
    }
    setCurrentStepIndex(STEP_ORDER.indexOf('result'));
  };
  const handleRestart = () => {
    setCurrentStepIndex(0);
    setExerciseAnswers({});
    setQuizAnswers({});
    setExerciseChecked(false);
    setQuizChecked(false);
    setXpEarned(0);
    setAccuracy(0);
  };

  if (isLocked) {
    return (
      <div className="max-w-2xl mx-auto flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
          <Lock size={28} className="text-muted-foreground" />
        </div>
        <h2 className="text-xl font-700 text-foreground mb-2">
          {language === 'fr' ? 'Leçon verrouillée' : 'Урок заблокирован'}
        </h2>
        <p className="text-sm text-muted-foreground mb-6">
          {language === 'fr' ? 'Complétez les leçons précédentes pour débloquer celle-ci.' : 'Завершите предыдущие уроки, чтобы разблокировать этот.'}
        </p>
        <Link href="/learning-path-screen" className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-600 hover:bg-primary/90 transition-all active:scale-95">
          <BookOpen size={15} />
          {language === 'fr' ? 'Voir le parcours' : 'Посмотреть путь'}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-slide-up">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground font-500">
            {levelMeta ? `${language === 'fr' ? 'Niveau' : 'Уровень'} ${levelMeta.number} — ${levelMeta.title[language]}` : ''}
          </p>
          <h1 className="text-lg font-700 text-foreground">{lesson.title[language]}</h1>
        </div>
        <Link href="/learning-path-screen" className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
          <X size={16} />
        </Link>
      </div>

      <LessonProgressBar steps={STEP_ORDER} currentIndex={currentStepIndex} language={language} />

      <div className="bg-card rounded-2xl border border-border card-shadow p-6 min-h-[400px]">
        {currentStepType === 'theory' && currentStepData?.type === 'theory' && (
          <TheoryStep step={currentStepData as any} language={language} />
        )}
        {currentStepType === 'example' && currentStepData?.type === 'example' && (
          <ExampleStep step={currentStepData as any} language={language} />
        )}
        {currentStepType === 'exercise' && currentStepData?.type === 'exercise' && (
          <ExerciseStep step={currentStepData as any} language={language} onCheck={handleExerciseCheck} checked={exerciseChecked} userAnswers={exerciseAnswers} />
        )}
        {currentStepType === 'quiz' && currentStepData?.type === 'quiz' && (
          <QuizStep step={currentStepData as any} language={language} onComplete={handleQuizComplete} checked={quizChecked} userAnswers={quizAnswers} />
        )}
        {currentStepType === 'result' && (
          <ResultStep language={language} xpEarned={xpEarned} accuracy={accuracy} lessonTitle={lesson.title[language]} onRestart={handleRestart} levelNumber={levelMeta?.number} allLessonsCompleted={levelMeta ? levelMeta.lessons.every((ls) => ls.status === 'completed' || ls.id === lessonId) : false} />
        )}
      </div>

      {currentStepType !== 'quiz' && currentStepType !== 'result' && (
        <div className="flex items-center justify-between">
          <button onClick={handleBack} disabled={currentStepIndex === 0} className="px-5 py-2.5 rounded-xl border border-border text-sm font-600 text-muted-foreground hover:bg-secondary hover:text-foreground transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95">
            {t(language, 'lesson_back')}
          </button>
          <button onClick={handleNext} disabled={currentStepType === 'exercise' && !exerciseChecked} className="px-6 py-2.5 rounded-xl bg-primary text-white text-sm font-600 hover:bg-primary/90 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95">
            {t(language, 'lesson_next')}
          </button>
        </div>
      )}
    </div>
  );
}